import { ReactNode } from 'react';

import { FOCO_SOBRE_AZUL, FOCO_VISIVEL } from 'utils/foco';

interface HeroDoCatalogoProps {
  regulamentoUrl: string;
  /** Caixa do prazo, posta abaixo do texto. */
  prazo: ReactNode;
}

const ICONE = { width: 19, height: 19, viewBox: '0 0 24 24', fill: 'none', stroke: 'var(--brand-white)', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const BENEFICIOS = [
  {
    texto: 'Experiências enriquecem a vida e significam investir em si mesmo.',
    icone: <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />,
  },
  {
    texto: 'Inspiram novas formas de ver o mundo e viver a vida.',
    icone: (
      <>
        <path d="M9 18h6" />
        <path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 0-4 12.7V18h8v-3.3A7 7 0 0 0 12 2Z" />
      </>
    ),
  },
  {
    texto: 'Criam memórias duradouras e expandem os horizontes.',
    icone: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 14s1.5 2 4 2 4-2 4-2" />
        <path d="M9 9h.01" />
        <path d="M15 9h.01" />
      </>
    ),
  },
];

/** Hero azul "Viva o que importa!", com o prazo e a caixa "Porque a vida é feita de experiências!" (CAT-02). */
export default function HeroDoCatalogo({ regulamentoUrl, prazo }: HeroDoCatalogoProps) {
  return (
    <section
      aria-labelledby="titulo-hero-catalogo"
      className="mb-[26px] overflow-hidden rounded-[20px] bg-brand-blue px-5 pb-9 pt-[34px] text-brand-white sm:px-[38px]"
    >
      <div className="flex flex-wrap items-stretch gap-[30px]">
        <div className="min-w-0 flex-[1_1_470px]">
          <h2
            id="titulo-hero-catalogo"
            tabIndex={0}
            className={`m-0 mb-3.5 font-display text-[28px] font-extrabold leading-[1.1] text-brand-yellow sm:text-[34px] ${FOCO_SOBRE_AZUL}`}
          >
            Viva o que importa!
          </h2>
          <p tabIndex={0} className={`m-0 text-[15px] leading-[1.7] ${FOCO_SOBRE_AZUL}`}>
            O <b>Catálogo de Experiências</b> é um convite pra criar memórias e escolher momentos que fazem sentido pra você.
            Seja para celebrar, presentear ou viver algo novo. Acesse o regulamento do{' '}
            {/* SPEC_DEVIATION: link em branco (design amarelo, 4,44:1 sobre o azul em 15px). */}
            <a
              href={regulamentoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`font-bold text-brand-white underline hover:text-brand-white ${FOCO_SOBRE_AZUL}`}
            >
              Catálogo de Experiências aqui<span className="sr-only"> (abre em nova aba)</span>
            </a>
          </p>
          {prazo}
        </div>

        <aside
          aria-labelledby="titulo-beneficios"
          className="box-border flex max-w-full flex-[1_1_336px] flex-col rounded-xl bg-brand-yellow-light px-6 pb-2.5 pt-[22px] text-content md:max-w-[392px]"
        >
          <h3
            id="titulo-beneficios"
            tabIndex={0}
            className={`m-0 mb-1 font-display text-[21px] font-extrabold leading-[1.15] text-brand-blue-dark ${FOCO_VISIVEL}`}
          >
            Porque a vida é feita de experiências!
          </h3>
          <ul className="m-0 flex flex-1 list-none flex-col justify-center p-0">
            {BENEFICIOS.map(beneficio => (
              <li
                key={beneficio.texto}
                tabIndex={0}
                className={`flex items-start gap-3 rounded-[10px] border-0 border-t border-solid border-[rgba(51,51,189,0.13)] py-3.5 pl-1 pr-2.5 ${FOCO_VISIVEL}`}
              >
                <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-blue">
                  <svg {...ICONE} focusable="false">
                    {beneficio.icone}
                  </svg>
                </span>
                <p className="m-0 text-[13px] leading-[1.55]">{beneficio.texto}</p>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </section>
  );
}
